import React from 'react';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

type ServicesElectricalProps = {
  onBack: () => void;
};

export default function ServicesElectrical({ onBack }: ServicesElectricalProps): React.ReactElement {
  return (
    <section className="bg-white">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-5xl">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-colors"
          >
            <ArrowBackOutlinedIcon fontSize="small" />
            <span className="text-sm font-semibold">Back to Services</span>
          </button>
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-600 text-white text-sm font-semibold mb-4">
              Service Five
            </span>
            <h2 className="text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
              Electrical Installation & Maintenance
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg max-w-3xl mx-auto">
              In a world driven by uninterrupted power, Ancient Engineering delivers engineered electrical systems
              that prioritise safety, reliability, and long-term performance across all sectors.
            </p>
          </div>

          {/* Sector-Specific Electrical Engineering */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
              Sector-Specific Electrical Engineering
            </h3>
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Residential Electrical Systems */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <HomeOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Residential Electrical Systems</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600 mb-3">
                  From single-phase homes to high-end smart residences, we design and install electrical systems
                  that are safe, scalable, and future-ready.
                </p>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Complete house wiring and rewiring</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Lighting and power distribution</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Backup power integration (inverters & generators)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Fault protection and earthing systems</span>
                  </li>
                </ul>
              </div>

              {/* Commercial & Industrial Electrical Services */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <BusinessOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Commercial & Industrial Electrical Services</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600 mb-3">
                  We support factories, offices, and industrial facilities with robust electrical infrastructure
                  designed to handle continuous, high-load operations.
                </p>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Three-phase power installations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Distribution boards and control panels</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Machinery and equipment connections</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Preventive maintenance to minimise downtime</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Energy efficiency optimisation</span>
                  </li>
                </ul>
              </div>

              {/* Agriculture & Utility Electrical Solutions */}
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <AgricultureOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Agriculture & Utility Electrical Solutions</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600 mb-3">
                  Reliable electrical systems tailored for agricultural operations and utility infrastructure.
                </p>
                <ul className="space-y-2 text-sm leading-relaxed text-slate-600">
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Electrical installations for irrigation systems</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Cold storage and processing facilities</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Pump stations and agricultural buildings</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                    <span>Power systems for remote and off-grid sites</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* End-to-End Electrical Project Management */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-slate-900 mb-6 text-center">
              End-to-End Electrical Project Management
            </h3>
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <EngineeringOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Electrical System Design & Planning</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  Detailed load calculations, circuit design, and protection planning to ensure optimal performance
                  and compliance.
                </p>
              </div>

              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <BuildOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Professional Installation & Commissioning</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  All systems undergo rigorous testing, inspection, and commissioning to ensure they meet safety
                  regulations and operational standards before handover.
                </p>
              </div>

              <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
                    <VerifiedOutlinedIcon fontSize="medium" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">Ongoing Maintenance & Support</h4>
                </div>
                <p className="text-sm leading-relaxed text-slate-600">
                  Scheduled inspections and rapid-response maintenance services to extend system lifespan and prevent
                  costly failures.
                </p>
              </div>
            </div>
          </div>

          {/* Why Choose Our Electrical Services */}
          <div className="rounded-2xl bg-sky-600 p-8 lg:p-10 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <ElectricalServicesOutlinedIcon className="text-white" fontSize="large" />
              <h4 className="text-2xl font-semibold text-white">Why Choose Our Electrical Services?</h4>
            </div>
            <p className="text-base leading-relaxed text-white/90 max-w-2xl mx-auto">
              We don't just wire buildings — we engineer electrical systems built to last. Our installations use
              high-grade cables, protection devices, and industry-approved components to ensure safety, efficiency,
              and long-term reliability.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
